import { useCallback, useMemo } from 'react'
import { StringParam, useQueryParam, withDefault } from 'use-query-params'

import type {
  AnalyticsTableColumn,
  AnalyticsTableSorting,
} from '../analyticsModule/components/AnalyticsTable/AnalyticsTable.types'

type UseReportTableSortingParams = {
  columns: AnalyticsTableColumn[]
  defaultSorting?: AnalyticsTableSorting
}

export const useReportTableSorting = ({ columns, defaultSorting }: UseReportTableSortingParams) => {
  const [sortParam, setSortParam] = useQueryParam('sort', withDefault(StringParam, undefined))
  const [orderParam, setOrderParam] = useQueryParam('order', withDefault(StringParam, 'desc'))

  const availableColumns = useMemo(() => {
    return columns.filter((column) =>
      ['money', 'number', 'date', 'string', 'percentage'].includes(column.type),
    )
  }, [columns])

  const availableColumnIds = useMemo(() => {
    return availableColumns.map((column) => column.id)
  }, [availableColumns])

  const sorting = useMemo((): AnalyticsTableSorting | undefined => {
    if (!sortParam || !availableColumnIds.includes(sortParam)) {
      if (defaultSorting && availableColumnIds.includes(defaultSorting.columnId)) {
        return defaultSorting
      }

      return undefined
    }

    const direction: 'asc' | 'desc' = orderParam === 'asc' ? 'asc' : 'desc'

    return {
      columnId: sortParam,
      direction,
    }
  }, [sortParam, availableColumnIds, orderParam, defaultSorting])

  const onSortingChange = useCallback(
    (columnId: string) => {
      if (!availableColumnIds.includes(columnId)) {
        return
      }

      if (!sorting || sorting.columnId !== columnId) {
        setSortParam(columnId)
        setOrderParam('desc')

        return
      }

      if (!sortParam && sorting.columnId === columnId) {
        setSortParam(columnId)
      }

      const newDirection = sorting.direction === 'desc' ? 'asc' : 'desc'

      setOrderParam(newDirection)
    },
    [availableColumnIds, sorting, sortParam, setOrderParam, setSortParam],
  )

  return {
    sorting,
    onSortingChange,
  }
}
